/**
 * Generated Training Page
 *
 * Displays AI-generated training videos from Veo 3.1.
 * Shows generation job history with status, progress, and results.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles,
  Video,
  Clock,
  User,
  FileText,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertCircle,
  RefreshCw,
  ChevronRight,
  Settings,
  BarChart3,
} from 'lucide-react';
import {
  useTrainingGenerationJobs,
  useTrainingGenerationStats,
  useTrainingGenerationConfig,
  useCancelTrainingGeneration,
  getStatusColor,
  getStatusText,
  formatDuration,
  formatRelativeTime,
  type TrainingGenerationJob,
} from '../../hooks/useTrainingGeneration';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';

export function GeneratedTrainingPage() {
  useDocumentTitle('AI Generated Training');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  const { data: jobsResponse, isLoading, refetch, isRefetching } = useTrainingGenerationJobs({
    page,
    limit: 20,
    status: statusFilter,
  });

  const { data: stats } = useTrainingGenerationStats();
  const { data: config } = useTrainingGenerationConfig();

  const jobs = jobsResponse?.data ?? [];
  const pagination = jobsResponse?.pagination;

  const statusOptions = [
    { value: undefined, label: 'All Status' },
    { value: 'queued', label: 'Queued' },
    { value: 'generating', label: 'Generating' },
    { value: 'uploading', label: 'Uploading' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="text-purple-600" size={28} />
            AI-Generated Training
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Training videos automatically generated for skill gaps
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isRefetching}
          className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw size={16} className={isRefetching ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-5">
          <StatCard
            label="Total Jobs"
            value={stats.total}
            icon={<BarChart3 size={20} />}
            color="gray"
          />
          <StatCard
            label="In Progress"
            value={(stats.byStatus.queued || 0) + (stats.byStatus.generating || 0) + (stats.byStatus.uploading || 0)}
            icon={<Loader2 size={20} className="animate-spin" />}
            color="blue"
          />
          <StatCard
            label="Completed"
            value={stats.byStatus.completed || 0}
            icon={<CheckCircle2 size={20} />}
            color="emerald"
          />
          <StatCard
            label="Failed"
            value={stats.byStatus.failed || 0}
            icon={<XCircle size={20} />}
            color="red"
          />
          <StatCard
            label="Recent (30d)"
            value={stats.recentCompletions}
            icon={<Clock size={20} />}
            color="purple"
          />
        </div>
      )}

      {/* Config Info */}
      {config && (
        <div className="rounded-xl border border-purple-100 bg-purple-50 p-4">
          <div className="flex items-center gap-2 text-sm">
            <Settings size={16} className="text-purple-600" />
            <span className="font-medium text-purple-900">Auto-Generation Settings:</span>
            <span className="text-purple-700">
              Triggers when score &lt; {config.autoGenerateThreshold}% after{' '}
              {config.minAssessmentsBeforeGeneration} assessments
            </span>
            <span className="mx-2 text-purple-300">|</span>
            <span className="text-purple-700">
              Video duration: {formatDuration(config.defaultDurationSeconds)}
            </span>
            <span className="mx-2 text-purple-300">|</span>
            <span className="text-purple-700 capitalize">Provider: {config.provider}</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <select
          value={statusFilter ?? ''}
          onChange={(e) => {
            setStatusFilter(e.target.value || undefined);
            setPage(1);
          }}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:ring-purple-500"
        >
          {statusOptions.map((opt) => (
            <option key={opt.value ?? 'all'} value={opt.value ?? ''}>
              {opt.label}
            </option>
          ))}
        </select>

        {pagination && (
          <span className="text-sm text-gray-500">
            Showing {jobs.length} of {pagination.total} jobs
          </span>
        )}
      </div>

      {/* Jobs List */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 py-16">
          <Sparkles className="mb-4 text-gray-400" size={48} />
          <h3 className="text-lg font-medium text-gray-900">No training generations yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            AI training videos will appear here when generated for skill gaps
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <JobCard key={job.id} job={job} onRefresh={() => refetch()} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page === pagination.totalPages}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

interface StatCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: 'gray' | 'blue' | 'emerald' | 'red' | 'purple';
}

function StatCard({ label, value, icon, color }: StatCardProps) {
  const colorClasses = {
    gray: 'bg-gray-50 text-gray-600',
    blue: 'bg-blue-50 text-blue-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <div className={`rounded-xl p-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <span className="text-2xl font-bold">{value}</span>
        {icon}
      </div>
      <p className="mt-1 text-sm opacity-80">{label}</p>
    </div>
  );
}

interface JobCardProps {
  job: TrainingGenerationJob;
  onRefresh?: () => void;
}

function JobCard({ job, onRefresh }: JobCardProps) {
  const [showPrompt, setShowPrompt] = useState(false);
  const statusColor = getStatusColor(job.status);
  const statusText = getStatusText(job.status);
  const cancelMutation = useCancelTrainingGeneration();

  const canCancel = ['queued', 'generating', 'uploading'].includes(job.status);

  const handleCancel = async () => {
    if (!confirm('Are you sure you want to cancel this training generation job?')) return;
    try {
      await cancelMutation.mutateAsync(job.id);
      onRefresh?.();
    } catch (error) {
      console.error('Failed to cancel job:', error);
    }
  };

  return (
    <div className="group rounded-xl border border-gray-100 bg-white p-5 shadow-sm transition-all hover:shadow-md hover:border-gray-200">
      <div className="flex items-start justify-between gap-4">
        {/* Left: Main Info */}
        <div className="min-w-0 flex-1">
          {/* Status & Time */}
          <div className="flex items-center gap-3 mb-3">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${statusColor}`}>
              {job.status === 'generating' && <Loader2 size={12} className="animate-spin" />}
              {job.status === 'completed' && <CheckCircle2 size={12} />}
              {job.status === 'failed' && <XCircle size={12} />}
              {statusText}
            </span>
            <span className="text-xs text-gray-500">{formatRelativeTime(job.createdAt)}</span>
            {canCancel && (
              <button
                onClick={handleCancel}
                disabled={cancelMutation.isPending}
                className="ml-auto text-xs text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
              >
                {cancelMutation.isPending ? 'Cancelling...' : 'Cancel'}
              </button>
            )}
          </div>

          {/* Operator & SOP */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            {job.operator && (
              <div className="flex items-center gap-1.5 text-gray-700">
                <User size={14} className="text-gray-400" />
                <span className="font-medium">{job.operator.name}</span>
                <span className="text-gray-400">({job.operator.employeeId})</span>
              </div>
            )}
            {job.sop && (
              <div className="flex items-center gap-1.5 text-gray-600">
                <FileText size={14} className="text-gray-400" />
                <span>{job.sop.title}</span>
              </div>
            )}
          </div>

          {/* Skill Gap */}
          {job.skillGapDescription && (
            <p className="mt-2 text-sm text-gray-600 line-clamp-2">
              <span className="font-medium text-gray-700">Skill Gap:</span>{' '}
              {job.skillGapDescription}
            </p>
          )}

          {/* Task */}
          {job.task && (
            <div className="mt-2 text-sm text-gray-600">
              <span className="font-medium text-gray-700">Task:</span> {job.task.title}
            </div>
          )}

          {/* Score */}
          <div className="mt-2 flex items-center gap-4 text-sm">
            <span className="text-gray-600">
              Score: <span className="font-medium text-red-600">{job.currentScore}%</span>
              {' → '}
              <span className="font-medium text-emerald-600">{job.targetScore}%</span> target
            </span>
            {job.videoDurationSeconds && (
              <span className="text-gray-500">
                <Video size={14} className="inline mr-1" />
                {formatDuration(job.videoDurationSeconds)}
              </span>
            )}
          </div>

          {/* Error Message */}
          {job.errorMessage && (
            <ErrorMessageDisplay message={job.errorMessage} />
          )}

          {/* Prompt Display (for debugging) */}
          {job.prompt && (
            <div className="mt-3">
              <button
                onClick={() => setShowPrompt(!showPrompt)}
                className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
              >
                <FileText size={12} />
                {showPrompt ? 'Hide' : 'Show'} AI Prompt
                <ChevronRight size={12} className={`transition-transform ${showPrompt ? 'rotate-90' : ''}`} />
              </button>
              {showPrompt && (
                <div className="mt-2 rounded-lg bg-gray-50 p-3 text-xs font-mono text-gray-600 whitespace-pre-wrap max-h-48 overflow-y-auto border border-gray-200">
                  {job.prompt}
                </div>
              )}
            </div>
          )}

          {/* Generated Material */}
          {job.generatedMaterial && (
            <div className="mt-3 flex items-center gap-3 rounded-lg bg-emerald-50 p-3">
              <Video size={18} className="text-emerald-600" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-emerald-900">{job.generatedMaterial.title}</p>
                {job.generatedAssignment && (
                  <p className="text-xs text-emerald-700">
                    Assigned to operator • Due: {job.generatedAssignment.dueDate
                      ? new Date(job.generatedAssignment.dueDate).toLocaleDateString()
                      : 'Not set'}
                  </p>
                )}
              </div>
              <Link
                to={`/learning`}
                className="flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-800"
              >
                View
                <ChevronRight size={16} />
              </Link>
            </div>
          )}
        </div>

        {/* Right: Score Badge */}
        <div className="flex flex-col items-center">
          <div
            className={`flex h-14 w-14 items-center justify-center rounded-full text-lg font-bold ${
              job.currentScore < 50
                ? 'bg-red-100 text-red-700'
                : job.currentScore < 70
                ? 'bg-amber-100 text-amber-700'
                : 'bg-emerald-100 text-emerald-700'
            }`}
          >
            {job.currentScore}%
          </div>
          <span className="mt-1 text-xs text-gray-500">Score</span>
        </div>
      </div>
    </div>
  );
}

// ============================================
// ERROR MESSAGE HELPERS
// ============================================

const ERROR_TRUNCATE_LENGTH = 200;

/**
 * Parse raw error messages (often JSON) into human-readable text.
 * Maps known error codes to friendly messages.
 */
function formatErrorMessage(msg: string): string {
  try {
    const parsed = JSON.parse(msg);
    // Handle { error: { code, message } } shape
    if (parsed?.error?.message) {
      const code = parsed.error.code;
      if (code === 429) return 'Rate limit exceeded — please try again later.';
      return String(parsed.error.message);
    }
    // Handle { message } shape
    if (parsed?.message) {
      return String(parsed.message);
    }
    // Handle { error: "string" } shape
    if (typeof parsed?.error === 'string') {
      return parsed.error;
    }
  } catch {
    // Not JSON — return as-is
  }
  return msg;
}

function ErrorMessageDisplay({ message }: { message: string }) {
  const [expanded, setExpanded] = useState(false);
  const formatted = formatErrorMessage(message);
  const isLong = formatted.length > ERROR_TRUNCATE_LENGTH;

  const displayText = !expanded && isLong
    ? formatted.slice(0, ERROR_TRUNCATE_LENGTH) + '...'
    : formatted;

  return (
    <div className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 max-h-24 overflow-y-auto">
      <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
      <div className="min-w-0">
        <span>{displayText}</span>
        {isLong && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="ml-1 font-medium underline hover:text-red-900"
          >
            {expanded ? 'show less' : 'show more'}
          </button>
        )}
      </div>
    </div>
  );
}
